import { ANALYTICS_SOURCE, AT_A_GLANCE_ACTION_SUBJECT_ID } from '../analytics.ts';

/**
 * Why the cc-smarts `twg-hub-summaries` request did not produce a usable
 * overview. `httpError` is a non-2xx response, `malformedResponse` is a 2xx
 * whose envelope we could not parse, and `networkError` is a rejected fetch
 * (DNS, offline, CORS). Aborts are not failures and are never reported.
 */
export type AtAGlanceFailureReason =
	| 'httpError'
	| 'malformedResponse'
	| 'networkError'
	| 'rateLimited';

export type AtAGlanceSucceededAttributes = {
	overviewCount: number;
	summariesCount: number;
	incomingLinkCount: number;
	outgoingLinkCount: number;
	durationMs: number;
	retryCount?: number;
};

export type AtAGlanceFailedAttributes = {
	reason: AtAGlanceFailureReason;
	statusCode?: number;
	incomingLinkCount: number;
	outgoingLinkCount: number;
	durationMs: number;
	retryCount?: number;
};

export type AtAGlanceAbortedAttributes = {
	incomingLinkCount: number;
	outgoingLinkCount: number;
	durationMs: number;
	retryCount?: number;
};

export const getAtAGlanceSucceededEvent = (
	attributes: AtAGlanceSucceededAttributes,
): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'succeeded';
		readonly actionSubject: 'atAGlanceSummary';
		readonly actionSubjectId: 'teamworkGraphAtAGlance';
		readonly source: 'byline-connections';
		readonly attributes: AtAGlanceSucceededAttributes;
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'succeeded',
			actionSubject: 'atAGlanceSummary',
			actionSubjectId: AT_A_GLANCE_ACTION_SUBJECT_ID,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

export const getAtAGlanceFailedEvent = (
	attributes: AtAGlanceFailedAttributes,
): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'failed';
		readonly actionSubject: 'atAGlanceSummary';
		readonly actionSubjectId: 'teamworkGraphAtAGlance';
		readonly source: 'byline-connections';
		readonly attributes: AtAGlanceFailedAttributes;
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'failed',
			actionSubject: 'atAGlanceSummary',
			actionSubjectId: AT_A_GLANCE_ACTION_SUBJECT_ID,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;

/**
 * Fired when an in-flight request is torn down before it settled — the modal or
 * preview panel closed, or the connection data changed and superseded it. This
 * is our own cancellation, not a backend outcome, so it is kept separate from
 * `succeeded`/`failed` to avoid skewing the success rate. `durationMs` is how
 * long the request had been running when it was aborted.
 */
export const getAtAGlanceAbortedEvent = (
	attributes: AtAGlanceAbortedAttributes,
): {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: 'aborted';
		readonly actionSubject: 'atAGlanceSummary';
		readonly actionSubjectId: 'teamworkGraphAtAGlance';
		readonly source: 'byline-connections';
		readonly attributes: AtAGlanceAbortedAttributes;
	};
} =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'aborted',
			actionSubject: 'atAGlanceSummary',
			actionSubjectId: AT_A_GLANCE_ACTION_SUBJECT_ID,
			source: ANALYTICS_SOURCE,
			attributes,
		},
	}) as const;
